import React, { useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    Grid,
    Typography,
    InputAdornment,
    Card,
    CardContent,
    CardActions,
    Box,
    Tooltip,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import Loading from "../components/common/Loading";
import AppSnackbar from "../common/AppSnackbar";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { useReservationAdminAPI } from "../service/useReservationAdminAPI";
import { useNavigate } from "react-router-dom";
import {formatDate} from "../common/DateUtil";

// Helper for stacked label/value (nice on mobile)
const InfoRow = ({ label, value }) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>{label}</Typography>
        <Typography>{value || "-"}</Typography>
    </Box>
);

const Animals = () => {
    const { t } = useTranslation("");
    const st = (key, options) => t(`animalsPage.${key}`, options);

    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down("sm"));

    const [inputValue, setInputValue] = useState("");
    const { searchByAnimalName } = useReservationAdminAPI();
    const [animalSearchResult, setAnimalSearchResult] = useState([]);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [severity] = useState("error");
    const navigate = useNavigate();

    const handleAddOption = () => {
        if (inputValue) {
            searchByAnimalName(inputValue)
                .then((value) => setAnimalSearchResult(value))
                .catch(() => {
                    handleFetchError(t("searchFailure"));
                });
        }
    };

    function handleFetchError(reason) {
        setSnackbarMessage(reason);
        setOpenSnackbar(true);
    }

    const handleInputChange = (event) => {
        setInputValue(event.target.value);
    };

    // Mobile card renderer with truncation for long text
    const TwoLineClamp = ({ text }) => (
        <Typography sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
        }}>
            {text || "-"}
        </Typography>
    );

    return (
        <Grid container justifyContent="center" sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 } }}>
            <Grid item xs={12} sm={12} sx={{p: 2, m: 2}}>
                <Typography variant={isXs ? "h6" : "h5"} sx={{ mb: 2 }}>
                    {st("title")}
                </Typography>

                <TextField
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddOption();
                        }
                    }}
                    fullWidth={isXs}
                    required
                    variant="outlined"
                    sx={{ backgroundColor: "white", mb: 2, maxWidth: isXs ? "100%" : 420 }}
                    value={inputValue}
                    onChange={handleInputChange}
                    label={st("searchLabel")}
                    inputProps={{ "data-testid": "animal-search-input" }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Button onClick={handleAddOption} aria-label={st("searchLabel")} data-testid="animal-search-button">
                                    <SearchIcon />
                                </Button>
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Mobile: cards, Desktop: table */}
                {isXs ? (
                    <Grid container spacing={1}>
                        {animalSearchResult.length > 0 ? (
                            animalSearchResult.map((result, idx) => (
                                <Grid item xs={12} key={result?.animal?.uuid || idx}>
                                    <Card variant="outlined">
                                        <CardContent sx={{ display: "grid", gap: 1 }}>
                                            <InfoRow label={st("name")} value={result?.animal?.name} />
                                            <InfoRow label={st("birthDate")} value={formatDate(result?.animal?.birthDate)} />
                                            <InfoRow label={st("owner")} value={`${result?.person?.forename ?? ""} ${result?.person?.name ?? ""}`} />
                                            <Box>
                                                <Typography variant="caption" sx={{ opacity: 0.8 }}>{st("diet")}</Typography>
                                                <TwoLineClamp text={result?.animal?.diet} />
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" sx={{ opacity: 0.8 }}>{st("medicalHistorie")}</Typography>
                                                <TwoLineClamp text={result?.animal?.medicalHistory} />
                                            </Box>
                                            <InfoRow label={st("contactOtherAnimals")} value={result?.animal?.contactOtherAnimals ? st('yes') : st('no')} />
                                        </CardContent>
                                        <CardActions sx={{ justifyContent: "flex-end" }}>
                                            <Button onClick={() => navigate(result?.animal?.uuid)} data-testid={`view-animal-detail-button-${result?.animal?.name}`}>{st("viewDetail")}</Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))
                        ) : (
                            <Grid item xs={12}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography color="text.secondary" align="center">
                                            {st("noData")}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                ) : (
                    <TableContainer component={Paper}>
                        <Table size="small" aria-label="animals table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{st("name")}</TableCell>
                                    <TableCell>{st("birthDate")}</TableCell>
                                    <TableCell>{st("owner")}</TableCell>
                                    <TableCell>{st("diet")}</TableCell>
                                    <TableCell>{st("medicalHistorie")}</TableCell>
                                    <TableCell>{st("contactOtherAnimals")}</TableCell>
                                    <TableCell>{st("actions")}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {animalSearchResult.length > 0 ? (
                                    animalSearchResult.map((result, index) => (
                                        <TableRow key={result?.animal?.uuid || index}>
                                            <TableCell>{result?.animal?.name}</TableCell>
                                            <TableCell>{formatDate(result?.animal?.birthDate)}</TableCell>
                                            <TableCell>{`${result?.person?.forename ?? ''} ${result?.person?.name ?? ''}`}</TableCell>
                                            <TableCell>
                                                <Tooltip title={result?.animal?.diet || ''} arrow>
                                                    <Typography noWrap>
                                                        {!result?.animal?.diet ? '-' : result?.animal?.diet?.slice(0, 20)}
                                                        {result?.animal?.diet?.length > 20 ? '…' : ''}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={result?.animal?.medicalHistory || ''} arrow>
                                                    <Typography noWrap>
                                                        {!result?.animal?.medicalHistory ? '-' : result?.animal?.medicalHistory?.slice(0, 20)}
                                                        {result?.animal?.medicalHistory?.length > 20 ? '…' : ''}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>{result?.animal?.contactOtherAnimals ? st('yes') : st('no')}</TableCell>
                                            <TableCell>
                                                <Button onClick={() => { navigate(result?.animal?.uuid); }} color="primary" data-testid={`view-animal-detail-button-${result?.animal?.name}`}>
                                                    {st('viewDetail')}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography color="text.secondary">{st('noData')}</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Grid>

            <AppSnackbar
                openSnackbar={openSnackbar}
                snackbarMessage={snackbarMessage}
                setOpenSnackbar={setOpenSnackbar}
                severity={severity}
            />
        </Grid>
    );
};

export default withAuthenticationRequired(Animals, {
    onRedirecting: () => <Loading />,
    returnTo: "/beheer/login",
});
